import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useApolloClient, useMutation, useQuery } from '@apollo/client'
import UserContext from '../context/UserContext'
import {
  GET_IDEAS_QUERY, ADD_IDEA_MUTATION, UPDATE_IDEA_MUTATION, DELETE_IDEA_MUTATION, ASK_ABOUT_IDEA_MUTATION,
  LINK_IDEA_TO_TASK_MUTATION, QUICK_ADD_TASK_MUTATION, GET_PROJECTS_QUERY,
} from '../query/gqlQueries'
import MediaCorner from './MediaCorner'
import s from './IdeasView.module.scss'

// The Work app's ideas, on the website (server: schema/idea.js) — everyone's,
// as the team sees them in the app: something small to come back to, tied to a
// project. Jot one down, hand it to the Mac Studio, make it a task, or put it
// away when done. In the Work app's own look (`work-paper`): cream page, white
// cards, amber for ideas.

// Photo links come signed from the GraphQL server, built on its own idea of
// where the image server is — which can be wrong (localhost:4001 when it isn't
// told). The signature covers the path, not the host, so the origin is swapped
// for this site's image server, as the media pages do.
const IMAGE_STORE_URI = process.env.REACT_APP_IMAGE_STORE_URI
function storeUrl(url) {
  if (!url || !IMAGE_STORE_URI) return url
  try {
    const u = new URL(url)
    return IMAGE_STORE_URI.replace(/\/$/, '') + u.pathname + u.search
  } catch {
    return url
  }
}

export const IDEA_KINDS = {
  idea: { label: 'Idea', icon: '💡' },
  lookInto: { label: 'Look into', icon: '🔍' },
  discuss: { label: 'Discuss', icon: '💬' },
  ask: { label: 'Ask AI', icon: '✨' },
}
const STATUSES = [['open', 'Open'], ['done', 'Done'], ['all', 'All']]
const STATUS_NAME = { open: 'Open', done: 'Done', dropped: 'Not useful', task: 'Made a task' }
const POLL_MS = 4000

const when = (v) => { const t = v ? new Date(v) : null; return t && !isNaN(t) ? t.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : '' }

/** The Mac Studio's answer is markdown: headings, lists, bold — drawn plainly, never as HTML. */
function Answer({ text }) {
  const inline = (line) => line.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') ? <strong key={i}>{part.slice(2, -2)}</strong> : <React.Fragment key={i}>{part}</React.Fragment>)
  const blocks = []
  let list = null
  String(text || '').split('\n').forEach((raw, i) => {
    const line = raw.trimEnd()
    const item = /^\s*(?:[-*•]|\d+[.)])\s+(.*)$/.exec(line)
    if (item) { (list = list || []).push(<li key={i}>{inline(item[1])}</li>); return }
    if (list) { blocks.push(<ul key={`l${i}`}>{list}</ul>); list = null }
    if (!line.trim()) return
    const head = /^#{1,6}\s+(.*)$/.exec(line)
    blocks.push(head ? <h4 key={i}>{inline(head[1])}</h4> : <p key={i}>{inline(line)}</p>)
  })
  if (list) blocks.push(<ul key="last">{list}</ul>)
  return <div className={s.answer}>{blocks}</div>
}

export default function IdeasView({ onClose }) {
  const { username } = useContext(UserContext)
  const client = useApolloClient()
  const [status, setStatus] = useState('open')
  const [kind, setKind] = useState('')
  const [project, setProject] = useState('')
  const [search, setSearch] = useState('')
  const [openId, setOpenId] = useState(null)

  const { data, loading, error, refetch, startPolling, stopPolling } = useQuery(GET_IDEAS_QUERY, {
    variables: { status: status === 'all' ? null : status === 'done' ? null : 'open', limit: 300 },
    fetchPolicy: 'cache-and-network', errorPolicy: 'all',
  })
  const projectsQ = useQuery(GET_PROJECTS_QUERY, { variables: { username }, skip: !username, errorPolicy: 'ignore' })
  const projectNames = useMemo(() => [...new Set((projectsQ.data?.getProjects || []).map(p => p.properties?.title).filter(Boolean))].sort(), [projectsQ.data])

  const all = useMemo(() => data?.getIdeas || [], [data])
  // Done: everything put away — done, not useful, or made a task.
  const ideas = useMemo(() => all
    .filter(i => status !== 'done' || i.properties.status !== 'open')
    .filter(i => !kind || i.properties.kind === kind)
    .filter(i => !project || i.properties.project === project)
    .filter(i => !search.trim() || `${i.properties.text} ${i.properties.answer || ''}`.toLowerCase().includes(search.trim().toLowerCase())),
  [all, status, kind, project, search])
  const projectsHere = useMemo(() => [...new Set(all.map(i => i.properties.project).filter(Boolean))].sort(), [all])

  // The Mac Studio answers on the server: look again until it has.
  const thinking = all.some(i => i.properties.answerStatus === 'thinking')
  useEffect(() => { if (thinking) { startPolling(POLL_MS); return () => stopPolling() } }, [thinking, startPolling, stopPolling])

  const opened = all.find(i => i.identity === openId) || null

  return (
    <div className={`work-paper ${s.view}`}>
      <MediaCorner onClose={onClose} />
      <div className={s.inner}>
        <header className={s.head}>
          <div className={s.eyebrow}>Work</div>
          <h1><span className={s.headIcon}>💡</span> Ideas</h1>
          <p className={s.lede}>Everyone's ideas from the Work app — jot one down, ask the Mac Studio, make it a task, or put it away.</p>
        </header>

        <Composer username={username} projects={projectNames} onAdded={() => refetch()} />

        <div className={s.filters}>
          <div className={s.segment} role="tablist">
            {STATUSES.map(([key, label]) => (
              <button key={key} role="tab" aria-selected={status === key} className={status === key ? s.segOn : ''} onClick={() => setStatus(key)}>{label}</button>
            ))}
          </div>
          <div className={s.chips}>
            <button className={!kind ? s.chipOn : s.chip} onClick={() => setKind('')}>All kinds</button>
            {Object.entries(IDEA_KINDS).map(([key, k]) => (
              <button key={key} className={kind === key ? s.chipOn : s.chip} onClick={() => setKind(kind === key ? '' : key)}>{k.icon} {k.label}</button>
            ))}
          </div>
          <select className={s.select} value={project} onChange={e => setProject(e.target.value)} aria-label="Project">
            <option value="">All projects</option>
            {projectsHere.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          <input className={s.search} placeholder="Search" value={search} onChange={e => setSearch(e.target.value)} />
        </div>

        {error && !data && <div className={s.error}>{/requireTeam|team/i.test(error.message) ? 'Ideas are for the Work team.' : error.message}</div>}
        {loading && !data && <div className={s.muted}>Loading ideas…</div>}
        {data && ideas.length === 0 && <div className={s.empty}>{all.length ? 'Nothing matches those filters.' : status === 'open' ? 'No open ideas — add one above.' : 'Nothing here yet.'}</div>}

        <div className={s.grid}>
          {ideas.map(i => <IdeaCard key={i.identity} idea={i} onOpen={() => setOpenId(i.identity)} />)}
        </div>
      </div>

      {opened && (
        <IdeaDetail idea={opened} me={username} projects={projectNames} client={client}
          onClose={() => setOpenId(null)} onChanged={() => refetch()} />
      )}
    </div>
  )
}

function IdeaCard({ idea, onOpen }) {
  const p = idea.properties
  const k = IDEA_KINDS[p.kind] || IDEA_KINDS.idea
  const closed = p.status !== 'open'
  return (
    <button className={`${s.card} ${closed ? s.cardClosed : ''}`} onClick={onOpen}>
      <span className={s.cardTop}>
        <span className={s.kind}><span className={s.kindIcon}>{k.icon}</span>{k.label}</span>
        {p.answerStatus === 'thinking' && <span className={s.badge}>Thinking…</span>}
        {p.answerStatus === 'ready' && <span className={s.badge}>Answered</span>}
        {closed && <span className={s.badgeMuted}>{STATUS_NAME[p.status] || p.status}</span>}
      </span>
      <span className={`${s.text} ${closed ? s.struck : ''}`}>{p.text}</span>
      {idea.photos?.length > 0 && (
        <span className={s.thumbs}>{idea.photos.slice(0, 3).map(ph => <img key={ph.name} src={storeUrl(ph.thumbUrl)} alt="" loading="lazy" />)}</span>
      )}
      <span className={s.meta}>
        {p.project && <span className={s.project}>{p.project}</span>}
        <span>{[p.username, when(p.createdAt), p.source === 'voice' ? 'said' : null].filter(Boolean).join(' · ')}</span>
      </span>
      {idea.task && <span className={s.taskLine}>✓ Task: {idea.task.title}</span>}
    </button>
  )
}

function Composer({ username, projects, onAdded }) {
  const [text, setText] = useState('')
  const [kind, setKind] = useState('idea')
  const [project, setProject] = useState('')
  const [msg, setMsg] = useState('')
  const [add, { loading }] = useMutation(ADD_IDEA_MUTATION)
  const save = async () => {
    if (!text.trim()) return
    setMsg('')
    try {
      await add({ variables: { input: { text: text.trim(), kind, project: project || null, source: 'text', ask: kind === 'ask' } } })
      setText(''); onAdded()
    } catch (e) { setMsg(e.message) }
  }
  return (
    <div className={s.composer}>
      <div className={s.label}>{kind === 'ask' ? 'Ask the Mac Studio' : 'Add an idea'}</div>
      <textarea className={s.input} rows={2} value={text} placeholder={kind === 'ask' ? 'What would you like to know?' : 'What’s the idea?'}
        onChange={e => setText(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) save() }} />
      <div className={s.composerRow}>
        <div className={s.chips}>
          {Object.entries(IDEA_KINDS).map(([key, k]) => (
            <button key={key} className={kind === key ? s.chipOn : s.chip} onClick={() => setKind(key)}>{k.icon} {k.label}</button>
          ))}
        </div>
        <select className={s.select} value={project} onChange={e => setProject(e.target.value)} aria-label="Project">
          <option value="">No project</option>
          {projects.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <button className={s.primary} disabled={!text.trim() || loading || !username} onClick={save}>{loading ? 'Adding…' : kind === 'ask' ? 'Ask' : 'Add'}</button>
      </div>
      {msg && <div className={s.error}>{msg}</div>}
    </div>
  )
}

function IdeaDetail({ idea, me, projects, client, onClose, onChanged }) {
  const p = idea.properties
  const k = IDEA_KINDS[p.kind] || IDEA_KINDS.idea
  const [text, setText] = useState(p.text)
  const [question, setQuestion] = useState('')
  const [msg, setMsg] = useState('')
  const [busy, setBusy] = useState('')
  const [update] = useMutation(UPDATE_IDEA_MUTATION)
  const [del] = useMutation(DELETE_IDEA_MUTATION)
  const [ask] = useMutation(ASK_ABOUT_IDEA_MUTATION)
  const [link] = useMutation(LINK_IDEA_TO_TASK_MUTATION)
  useEffect(() => { setText(p.text) }, [idea.identity])
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const act = async (label, fn) => {
    setBusy(label); setMsg('')
    try { await fn(); onChanged() } catch (e) { setMsg(e.message) }
    setBusy('')
  }
  const set = (input) => act('save', () => update({ variables: { identity: idea.identity, input } }))
  const saveText = () => { if (text.trim() && text.trim() !== p.text) set({ text: text.trim() }) }
  const makeTask = () => act('task', async () => {
    const due = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10)
    const res = await client.mutate({
      mutation: QUICK_ADD_TASK_MUTATION,
      variables: {
        title: p.text.length > 120 ? `${p.text.slice(0, 117)}…` : p.text, description: p.text, type: 'Admin', position: 'Backlog',
        priority: 'Medium', assignee: me, timeBudget: 0, timeSpent: 0, dueDate: `${due}T16:00:00`, username: me, project: p.project || null,
      },
    })
    const taskId = res.data?.addTask?.identity
    if (taskId) await link({ variables: { identity: idea.identity, taskId } })
  })

  return (
    <div className={s.scrim} onClick={onClose}>
      <aside className={s.sheet} onClick={e => e.stopPropagation()} aria-label="Idea">
        <div className={s.sheetHead}>
          <span className={s.kind}><span className={s.kindIcon}>{k.icon}</span>{k.label}</span>
          <span className={s.muted}>{[p.username, when(p.createdAt), STATUS_NAME[p.status]].filter(Boolean).join(' · ')}</span>
          <button className={s.close} onClick={onClose} aria-label="Close">✕</button>
        </div>

        <textarea className={s.sheetText} value={text} onChange={e => setText(e.target.value)} onBlur={saveText} rows={Math.min(8, Math.max(2, Math.ceil(text.length / 60)))} />

        <div className={s.row}>
          <label className={s.field}>Kind
            <select className={s.select} value={p.kind} onChange={e => set({ kind: e.target.value })}>
              {Object.entries(IDEA_KINDS).map(([key, kk]) => <option key={key} value={key}>{kk.icon} {kk.label}</option>)}
            </select>
          </label>
          <label className={s.field}>Project
            <select className={s.select} value={p.project || ''} onChange={e => set(e.target.value ? { project: e.target.value } : { clearProject: true })}>
              <option value="">No project</option>
              {[...new Set([...(p.project ? [p.project] : []), ...projects])].map(x => <option key={x} value={x}>{x}</option>)}
            </select>
          </label>
        </div>

        {idea.photos?.length > 0 && (
          <div className={s.photos}>
            {idea.photos.map(ph => <a key={ph.name} href={storeUrl(ph.fileUrl)} target="_blank" rel="noreferrer"><img src={storeUrl(ph.thumbUrl)} alt="" /></a>)}
          </div>
        )}

        {/* The Mac Studio: its answer, and asking it (again, or something more specific). */}
        <section className={s.askBox}>
          <div className={s.label}>The Mac Studio</div>
          {p.answerStatus === 'thinking' && <div className={s.muted}>Thinking about it… the answer appears here when it's ready.</div>}
          {p.answerStatus === 'failed' && <div className={s.error}>{p.answerError || 'It couldn’t answer that.'}</div>}
          {p.question && p.answer && <div className={s.question}>“{p.question}”</div>}
          {p.answer && <Answer text={p.answer} />}
          <div className={s.askRow}>
            <input className={s.input} placeholder={p.answer ? 'Ask something more…' : 'Ask about it (optional: a more specific question)'}
              value={question} onChange={e => setQuestion(e.target.value)} />
            <button className={s.secondary} disabled={busy === 'ask' || p.answerStatus === 'thinking'}
              onClick={() => act('ask', async () => { await ask({ variables: { identity: idea.identity, question: question.trim() || null } }); setQuestion('') })}>
              ✨ Ask
            </button>
          </div>
        </section>

        {idea.task && <div className={s.taskLine}>✓ Became a task: {idea.task.title}{idea.task.status ? ` (${idea.task.status.toLowerCase()})` : ''}</div>}

        <div className={s.actions}>
          {p.status === 'open' ? (
            <>
              <button className={s.primary} disabled={!!busy} onClick={makeTask}>{busy === 'task' ? 'Making…' : 'Make it a task'}</button>
              <button className={s.secondary} disabled={!!busy} onClick={() => set({ status: 'done' })}>✓ Done with it</button>
              <button className={s.secondary} disabled={!!busy} onClick={() => set({ status: 'dropped' })}>Not useful</button>
            </>
          ) : p.status !== 'task' && (
            <button className={s.secondary} disabled={!!busy} onClick={() => set({ status: 'open' })}>Open it again</button>
          )}
          {p.username === me && (
            <button className={s.danger} disabled={!!busy}
              onClick={() => { if (window.confirm('Delete this idea?')) act('delete', async () => { await del({ variables: { identity: idea.identity } }); onClose() }) }}>
              Delete
            </button>
          )}
        </div>
        {msg && <div className={s.error}>{msg}</div>}
      </aside>
    </div>
  )
}
